import React, { useEffect, useState } from 'react';
import Footer from '../../components/Common/Footer';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import ThankYou from '../../components/Common/ThankYou';
import PlanPurchase from '../../components/Common/PlanPurchase';
import Dialogs from '../../components/Common/Dialog';
import UpdateProfileSuccess from '../../components/Common/UpdateProfileSuccess';
import { UserSettingsModel, UserShippingAddress } from '../../interfaces/user.interface';
import { cancelUserSubscription, updateUserProfile } from './services/Profile.service';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import ProfileForm from './components/ProfileForm';
import { fetchTrainingVideos, fetchUserDetails } from '../../store/thunks/user.thunk';
import Modal from '../../components/Common/modal/Modal';

interface LoaderState {
  profileUpdate: boolean;
  removeSubscription: boolean;
}

export interface ProfileShippingAddressValues {
  shippingName: string;
  recipientName: string;
  shippingAddress1: string;
  shippingAddress2: string;
  shippingState: string;
  shippingCountry: string;
  shippingCity: string;
  shippingZip: string;
}

interface ProfileValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  plan: string;
  settings: UserSettingsModel;
  shippingAddress: ProfileShippingAddressValues;
}

const Profile: React.FC = () => {
  const navigateTo = useNavigate();
  const user = useSelector((state: RootState) => state.user.user);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [purchasePlan, setPurchasePlan] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showThanksNote, setShowThanksNote] = useState<boolean>(false);
  const [showSetupSuccess, setShowSetupSuccess] = useState<boolean>(false);
  const [isFirstInitial, setIsFirstInitial] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();

  const [loader, setLoader] = useState<LoaderState>({
    profileUpdate: false,
    removeSubscription: false,
  });

  const [values, setValues] = useState<ProfileValues>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    plan: '',
    settings: {
      USER_SETTING_MARKETING_EMAILS: false,
      USER_SETTING_PROMOTIONAL_EMAILS: false,
    },
    shippingAddress: {
      shippingName: '',
      recipientName: '',
      shippingAddress1: '',
      shippingAddress2: '',
      shippingState: '',
      shippingCountry: 'United States',
      shippingCity: '',
      shippingZip: '',
    },
  });

  useEffect(() => {
    if (user) {
      const shippingAddress: UserShippingAddress = user.shippingAddresses?.length
        ? user.shippingAddresses[0]
        : ({} as UserShippingAddress);
      setIsFirstInitial(!shippingAddress?.shippingZip);
      setValues({
        ...values,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        settings: user.settings || ({} as UserSettingsModel),
        shippingAddress: {
          ...values.shippingAddress,
          ...shippingAddress,
          shippingName: shippingAddress?.shippingName || '',
          recipientName:
            shippingAddress?.recipientName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '',
        },
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name in values.settings) {
      setValues((prevValues) => ({
        ...prevValues,
        settings: {
          ...prevValues.settings,
          [name]: value === 'true',
        },
      }));
    } else if (name in values.shippingAddress) {
      setValues((prevValues) => ({
        ...prevValues,
        shippingAddress: {
          ...prevValues.shippingAddress,
          [name]: value,
        },
      }));
    } else {
      setValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement> | any) => {
    if (e.preventDefault) {
      e.preventDefault();
    }

    // If we got a form data object from the ProfileForm component
    // if (e.shippingAddress) {
    //   // This is coming from our custom form submission with shipping address
    //   setValues((prevValues) => ({
    //     ...prevValues,
    //     ...e,
    //     shippingAddress: {
    //       ...prevValues.shippingAddress,
    //       ...e.shippingAddress,
    //     },
    //   }));
    // }

    setLoader({ ...loader, profileUpdate: true });

    // Make sure we use the updated values
    // const dataToSubmit = e.shippingAddress ? e : values;
    const dataToSubmit = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      plan: values.plan,
      settings: values.settings,
    };

    const isFinishSaveShippingAddress = !!user?.saveDefaultShippingAddress && isFirstInitial;
    await updateUserProfile(
      dataToSubmit,
      user,
      isFinishSaveShippingAddress ? (show) => setShowSetupSuccess(show) : null,
    );
    dispatch(fetchTrainingVideos());
    dispatch(fetchUserDetails({ email: user?.email || '' }));
    setLoader({ ...loader, profileUpdate: false });
  };

  const cancelSubscription = async () => {
    setOpenDialog(false);
    setLoader({ ...loader, removeSubscription: true });
    await cancelUserSubscription(user?.email || '', setPurchasePlan);
    setLoader({ ...loader, removeSubscription: false });
  };

  const goBack = () => {
    navigateTo(-1);
    setShowThanksNote(false);
  };

  return (
    <div className="App">
      <NavigationBar />
      {showThanksNote ? (
        <ThankYou goBack={goBack} />
      ) : (
        <div className="bg-storytan p-4 mx-auto">
          {purchasePlan ? (
            <PlanPurchase />
          ) : (
            <ProfileForm
              handleSubmit={handleSubmit}
              values={values}
              handleChange={handleChange}
              errors={errors}
              loader={loader}
              user={user}
            />
          )}
        </div>
      )}
      <Footer />
      <Dialogs
        openDialog={openDialog}
        handelClose={() => setOpenDialog(false)}
        handelProcess={cancelSubscription}
        confirmMessage="Are you sure you want to cancel Subscription? If subscription cancelled, you may no longer have access to the books you created."
      />
      {showSetupSuccess && (
        <Modal
          title={''}
          className={'global-absolute-popup-style small-notification'}
          showSubmitButton={false}
          showCloseButton={false}
        >
          <UpdateProfileSuccess submit={() => setShowSetupSuccess(false)}></UpdateProfileSuccess>
        </Modal>
      )}
    </div>
  );
};

export default Profile;
